import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, Menu, X, User, LogOut, LayoutDashboard, MessageCircle, ChevronDown, Camera, Bell as BellIcon, ShieldAlert, Cable } from "lucide-react";
import { useState, useEffect } from "react";
import Logo from "@/components/Logo";
import HeaderSearch from "@/components/HeaderSearch";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import { useSettings } from "@/lib/settings";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

const CATS = [
  { v: "camera", label: "CCTV Cameras", icon: Camera, desc: "IP, PTZ, dome, thermal" },
  { v: "alarm", label: "Alarm Systems", icon: ShieldAlert, desc: "Wireless & wired panels" },
  { v: "intercom", label: "Intercoms", icon: BellIcon, desc: "Video & audio door entry" },
  { v: "accessory,dvr", label: "Accessories", icon: Cable, desc: "DVR/NVR, switches, access" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const notif = useNotifications?.();
  const [open, setOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const loc = useLocation();
  const nav = useNavigate();
  const hasCustomLogo = !!settings?.logo_url;

  useEffect(() => { api.get("/brands").then(r => setBrands(Array.isArray(r.data) ? r.data : [])).catch(() => setBrands([])); }, []);

  const HEADER_H = hasCustomLogo ? "h-24" : "h-16";
  // if a custom logo is uploaded, keep the entire header dark so light text stays readable
  const headerClass = hasCustomLogo
    ? "sticky top-0 z-40 backdrop-blur-xl bg-eagle-ink border-b border-eagle-gold/20 text-white"
    : "sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/70";
  const linkBase = hasCustomLogo ? "text-white/70 hover:text-white hover:bg-white/10" : "text-muted-foreground hover:text-foreground hover:bg-accent/60";
  const linkActive = hasCustomLogo ? "text-white bg-white/10" : "text-foreground bg-accent";

  return (
    <header className={headerClass}>
      <div className={`max-w-7xl mx-auto px-5 lg:px-8 ${HEADER_H} flex items-center justify-between`}>
        <Link to="/" className="flex items-center" data-testid="header-home-link">
          <Logo size={hasCustomLogo ? 44 : 28} darkBackdrop={!hasCustomLogo} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link to="/" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === "/" ? linkActive : linkBase}`} data-testid="nav-home">Home</Link>
          <div className="relative group">
            <Link to="/products" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${loc.pathname.startsWith("/products") ? linkActive : linkBase}`} data-testid="nav-products">
              Products <ChevronDown className="h-3 w-3" />
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[720px] bg-popover text-popover-foreground border border-border rounded-2xl shadow-2xl p-6 grid grid-cols-5 gap-6">
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-eagle-gold mb-4 font-semibold">Categories</div>
                  <div className="grid grid-cols-1 gap-1">
                    {CATS.map(c => (
                      <Link key={c.v} to={`/products?category=${c.v}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary transition-colors group/item" data-testid={`mega-cat-${c.v}`}>
                        <div className="h-9 w-9 rounded-lg bg-eagle-gold/10 text-eagle-gold flex items-center justify-center shrink-0 group-hover/item:bg-eagle-gold group-hover/item:text-eagle-ink transition-colors">
                          <c.icon className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{c.label}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link to="/products" className="mt-4 block text-sm font-semibold text-eagle-gold hover:underline" data-testid="mega-shop-all">Shop all products →</Link>
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-eagle-gold mb-4 font-semibold">Shop by brand · {brands.length}</div>
                  {brands.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No brands yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-64 overflow-y-auto pr-2">
                      {(Array.isArray(brands) ? brands : []).map(b => (
                        <Link key={b.brand_id} to={`/products?brand_id=${b.brand_id}`} className="text-sm py-1.5 px-2 rounded hover:bg-secondary hover:text-eagle-gold truncate transition-colors" data-testid={`mega-brand-${b.brand_id}`}>
                          {b.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Link to="/quote" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === "/quote" ? linkActive : linkBase}`} data-testid="nav-quote">Get a Quote</Link>
          <Link to="/about" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === "/about" ? linkActive : linkBase}`} data-testid="nav-about">About</Link>
          <Link to="/contact" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === "/contact" ? linkActive : linkBase}`} data-testid="nav-contact">Contact</Link>
        </nav>

        <div className="flex items-center gap-3">
          <HeaderSearch dark={hasCustomLogo} />
          <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme"
            className={`relative w-11 h-11 rounded-full border flex items-center justify-center transition ${hasCustomLogo ? "border-white/20 hover:border-eagle-gold" : "border-border hover:border-eagle-gold"}`}>
            <Sun className={`h-4 w-4 absolute transition-all duration-500 ${theme === "dark" ? "opacity-0 rotate-90 scale-0" : "opacity-100 text-eagle-gold"}`} />
            <Moon className={`h-4 w-4 absolute transition-all duration-500 ${theme === "dark" ? "opacity-100 text-eagle-gold" : "opacity-0 -rotate-90 scale-0"}`} />
          </button>

          {user && user.role === "customer" && notif && (
            <Popover>
              <PopoverTrigger asChild>
                <button className={`relative w-11 h-11 rounded-full border flex items-center justify-center ${hasCustomLogo ? "border-white/20 hover:border-eagle-gold" : "border-border hover:border-eagle-gold"}`} data-testid="customer-notification-bell">
                  <BellIcon className="h-4 w-4" />
                  {notif.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-eagle-gold text-eagle-ink text-[10px] font-bold flex items-center justify-center">{notif.unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <div className="font-display font-semibold text-sm">Notifications</div>
                  {notif.unread > 0 && <Button variant="ghost" size="sm" onClick={notif.markAll}>Mark read</Button>}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notif.items.length === 0 ? <div className="p-6 text-sm text-center text-muted-foreground">All caught up.</div> :
                    notif.items.map(n => (
                      <div key={n.notification_id} className={`p-3 border-b border-border ${n.read ? "opacity-60" : ""}`}>
                        <div className="text-sm font-medium">{n.title}</div>
                        <div className="text-xs text-muted-foreground">{n.body}</div>
                      </div>
                    ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {user === undefined ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={hasCustomLogo ? "secondary" : "outline"} size="sm" data-testid="user-menu-trigger" className={`rounded-full ${hasCustomLogo ? "bg-white/10 text-white hover:bg-white/20 border-0" : ""}`}>
                  <User className="h-4 w-4 mr-2" /> {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => nav("/admin")}><LayoutDashboard className="h-4 w-4 mr-2" />Admin Dashboard</DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => nav("/dashboard")}><LayoutDashboard className="h-4 w-4 mr-2" />My Dashboard</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/dashboard?tab=chat")}><MessageCircle className="h-4 w-4 mr-2" />Messages</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }}><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login"><Button variant={hasCustomLogo ? "secondary" : "ghost"} size="sm" className={hasCustomLogo ? "bg-white/10 text-white hover:bg-white/20 border-0" : ""}>Sign in</Button></Link>
              <Link to="/quote"><Button size="sm" className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">Get a Quote</Button></Link>
            </div>
          )}
          <button className={`md:hidden w-11 h-11 rounded-full border flex items-center justify-center ${hasCustomLogo ? "border-white/20" : "border-border"}`} onClick={() => setOpen(v => !v)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {[{ to: "/", l: "Home" }, { to: "/products", l: "Products" }, { to: "/quote", l: "Get a Quote" }, { to: "/about", l: "About" }, { to: "/contact", l: "Contact" }].map(n => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-3 text-sm font-medium rounded-md hover:bg-accent">{n.l}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}